import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin, isClinicAdmin, isStaff, hasAnyPermission } from '../../utils/permissions';

import {useGetProfile , useLogout} from '../../hooks/useApi';
import {
  LayoutDashboard,
  Users,
  Camera,
  Images,
  GitCompare,
  LayoutGrid,
  Building2,
  UserCog,
  X,
  Shield,
  LogOut,
  User,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AnimatedProfileSheet from '../AnimatedProfileSheet';


const Sidebar = ({ isOpen, onClose }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);

  const { mutateAsync: logoutApi } = useLogout();

  const  handleLogout = async () => {
    await logoutApi();
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setIsProfileSheetOpen(true);
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'superadmin':
        return 'SuperAdmin';
      case 'clinic_admin':
        return 'Clinic Admin';
      case 'staff':
        return 'Staff';
      default:
        return role;
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const {data: profile, isLoading, error} = useGetProfile();
  const profileData = profile?.data?.[0];



  const navigation = [
    {
      name: 'Dashboard',
      href: isSuperAdmin(currentUser) ? '/superadmin-dashboard' : '/dashboard',
      icon: LayoutDashboard,
      show: true,
    },
    {
      name: 'Patients',
      href: '/patients',
      icon: Users,
      show: hasAnyPermission(currentUser, ['patient.view','patient.list','all']),
    },
    {
      name: 'Capture Photo',
      href: '/capture',
      icon: Camera,
      show: hasAnyPermission(currentUser, ['capture.photo','all']),
    },
    {
      name: 'Gallery',
      href: '/gallery',
      icon: Images,
      show: hasAnyPermission(currentUser, ['gallery.all','all']),
    },
    {
      name: 'Before & After',
      href: '/before-after',
      icon: GitCompare,
      show: hasAnyPermission(currentUser, ['before_after.create','all']),
    },
    {
      name: 'Collage',
      href: '/collage',
      icon: LayoutGrid,
      show: hasAnyPermission(currentUser, ['collage.create','all']),
    },
    {
      name: 'Clinic Management',
      href: '/clinics',
      icon: Building2,
      show: isSuperAdmin(currentUser) ,
    },
    {
      name: 'Staff Management',
      href: '/staff',
      icon: UserCog,
      show:  hasAnyPermission(currentUser, ['staff.view','staff.list','all']),
    },
  ];



  const visibleNav = navigation.filter(item => {
    
  
    return item.show;
  });

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed  top-0 left-0 bottom-0 w-72 sm:w-64 bg-white/70 backdrop-blur-md border-r border-gray-200/50 z-40 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Header with Logo/Branding */}
          <div className="p-4 border-b border-gray-200/50">
            <div className="flex items-center justify-between ">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-md">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-semibold text-gray-900">MedDoc Pro</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">Medical Documentation</p>
                </div>
              </div>
              {/* Close button for mobile */}
              <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1  overflow-y-auto p-4 space-y-2">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={false}
                  onClick={() => onClose()}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100/50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer with Profile */}
          <div className="p-4 border-t border-gray-200/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center gap-3 hover:bg-gray-100/50 p-3 rounded-xl">
                  <Avatar className="w-10 h-10 border-2 border-primary-500">
                    <AvatarFallback className="bg-gradient-to-br from-primary-500 to-primary-600 text-white font-semibold">
                      {getInitials(profileData?.name || 'User')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium text-gray-900">{profileData?.name}</p>
                    <p className="text-xs text-gray-500">{getRoleDisplay(profileData?.role=== 1 ? 'Super Admin' : profileData?.role === 2 ? 'Doctor' : 'Staff')}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white/95 backdrop-blur-md">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{profileData?.name}</span>
                    <span className="text-xs text-gray-500">{profileData?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer" onClick={handleProfileClick}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Animated Profile Sheet */}
      <AnimatedProfileSheet
        open={isProfileSheetOpen}
        onClose={() => setIsProfileSheetOpen(false)}
      />
    </>
  );
};

export default Sidebar;
